import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';
import aiAutomationApi from '../utils/aiAutomationApi';
import { getCid } from '../utils/encryptedStorage';
import { canUseCompanyFeature } from '../utils/companyFeatureAccess';
import ChatCard from './ChatCard';
import '../styles/chat.css';

const formatTimestamp = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const formatInterval = (seconds) => {
  const safeSeconds = Number(seconds || 0);
  if (!safeSeconds || Number.isNaN(safeSeconds)) return 'Not set';
  if (safeSeconds < 60) return `${safeSeconds}s`;
  if (safeSeconds < 3600) return `${Math.round(safeSeconds / 60)} min`;
  if (safeSeconds < 86400) {
    const hours = safeSeconds / 3600;
    return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} hr`;
  }
  const days = safeSeconds / 86400;
  return `${Number.isInteger(days) ? days : days.toFixed(1)} day`;
};

const getEntryTimestamp = (entry) => {
  if (!entry || typeof entry === 'string') return null;
  const value = entry.timestamp || entry.created_at || entry.updated_at || entry.completedAt || entry.completed_at || null;
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getLatestTimestamp = (entries = []) => {
  if (!Array.isArray(entries) || entries.length === 0) return null;
  let latest = null;
  entries.forEach((entry) => {
    const timestamp = getEntryTimestamp(entry);
    if (!timestamp) return;
    if (!latest || timestamp.getTime() > latest.getTime()) {
      latest = timestamp;
    }
  });
  return latest;
};

const normalizeCandidateStatus = (candidate) => (
  candidate?.funnel_stage ||
  candidate?.stage ||
  candidate?.status ||
  candidate?.candidate_status ||
  'sourced'
)
  .toString()
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const getCandidateTimestamp = (candidate) => {
  if (!candidate || typeof candidate !== 'object') return null;
  const value =
    candidate.updated_at ||
    candidate.updatedAt ||
    candidate.lastUpdated ||
    candidate.last_updated ||
    candidate.created_at ||
    candidate.createdAt ||
    candidate.timestamp ||
    null;
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const StatusChip = ({ tone = 'neutral', children, compact = false, className = '' }) => {
  const { isDark } = useTheme();
  const tones = {
    success: { background: 'rgba(34, 197, 94, 0.14)', color: isDark ? '#86EFAC' : '#16A34A', border: 'rgba(34, 197, 94, 0.28)' },
    active: { background: 'rgba(59, 130, 246, 0.16)', color: isDark ? '#93C5FD' : '#2563EB', border: 'rgba(59, 130, 246, 0.30)' },
    warning: { background: 'rgba(245, 158, 11, 0.15)', color: isDark ? '#FCD34D' : '#D97706', border: 'rgba(245, 158, 11, 0.30)' },
    danger: { background: 'rgba(239, 68, 68, 0.15)', color: isDark ? '#FCA5A5' : '#DC2626', border: 'rgba(239, 68, 68, 0.30)' },
    neutral: { background: 'rgba(100, 116, 139, 0.14)', color: isDark ? '#CBD5E1' : '#475569', border: 'rgba(148, 163, 184, 0.24)' },
  };

  const style = tones[tone] || tones.neutral;
  return (
    <span
      className={`inline-flex items-center rounded-full backdrop-blur-md ${compact ? 'px-2 py-0.5 text-[11px] font-semibold' : 'px-2.5 py-1 text-xs font-semibold'} ${className}`}
      style={{
        backgroundColor: style.background,
        color: style.color,
        border: `1px solid ${style.border}`,
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
        animation: 'statusCountPop .45s ease-out both',
      }}
    >
      {children}
    </span>
  );
};

const WorkflowStepIcon = ({ state = 'upcoming' }) => {
  if (state === 'complete') {
    return (
      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 6L9 17l-5-5" />
      </svg>
    );
  }

  if (state === 'active') {
    return (
      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
        <path d="M8 5v14l11-7z" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="8" />
    </svg>
  );
};

const InfoIcon = ({ label }) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const visible = open || hovered;

  const updatePosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(240, window.innerWidth - 24);
    const estimatedHeight = 84;
    const left = Math.max(12, Math.min(rect.left + (rect.width / 2) - (width / 2), window.innerWidth - width - 12));
    const showAbove = rect.bottom + estimatedHeight + 12 > window.innerHeight;
    setPosition({
      left,
      top: showAbove ? Math.max(12, rect.top - estimatedHeight - 8) : rect.bottom + 8,
      width,
    });
  };

  useEffect(() => {
    if (!visible) return undefined;
    updatePosition();
    const reposition = () => updatePosition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, label]);

  return (
    <span
      className="inline-flex"
      onMouseEnter={() => {
        updatePosition();
        setHovered(true);
      }}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label="Explain candidate count"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          updatePosition();
          setOpen((value) => !value);
        }}
        onBlur={() => setOpen(false)}
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold"
        style={{ borderColor: 'rgba(148, 163, 184, 0.34)', color: '#64748B', backgroundColor: 'rgba(255, 255, 255, 0.72)' }}
      >
        i
      </button>
      {visible && typeof document !== 'undefined' && createPortal(
        <span
          role="tooltip"
          className="rounded-lg border border-slate-200 bg-white p-2.5 text-left text-[11px] font-medium leading-4 text-slate-600 shadow-2xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            width: position.width,
            maxHeight: 160,
            overflowY: 'auto',
            zIndex: 10000,
            pointerEvents: 'none',
          }}
        >
          {label}
        </span>,
        document.body,
      )}
    </span>
  );
};

const SectionCard = ({
  title,
  status,
  timestamp,
  tone = 'neutral',
  children,
  onClick,
  ctaLabel,
  statusAsChip = false,
  compact = false,
  className = '',
  animateDelay = 0,
}) => {
  const { isDark } = useTheme();
  const compactBody = compact ? 'min-h-[108px]' : '';
  const CardElement = onClick ? 'button' : 'div';
  return (
    <CardElement
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`rounded-2xl border text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg ${compact ? 'px-3.5 py-2.5' : 'px-4 py-3'} ${compactBody} ${className}`}
      style={{
        borderColor:
          tone === 'active'
            ? 'rgba(59, 130, 246, 0.18)'
            : tone === 'success'
            ? 'rgba(34, 197, 94, 0.18)'
            : tone === 'warning'
            ? 'rgba(245, 158, 11, 0.18)'
            : tone === 'danger'
            ? 'rgba(239, 68, 68, 0.18)'
            : 'rgba(148, 163, 184, 0.16)',
        background:
          tone === 'active'
            ? isDark ? 'linear-gradient(135deg, rgba(30, 64, 175, 0.28), rgba(15, 23, 42, 0.76))' : 'linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(255, 255, 255, 0.20))'
            : tone === 'success'
            ? isDark ? 'linear-gradient(135deg, rgba(21, 128, 61, 0.24), rgba(15, 23, 42, 0.76))' : 'linear-gradient(135deg, rgba(34, 197, 94, 0.12), rgba(255, 255, 255, 0.20))'
            : tone === 'warning'
            ? isDark ? 'linear-gradient(135deg, rgba(180, 83, 9, 0.24), rgba(15, 23, 42, 0.76))' : 'linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(255, 255, 255, 0.20))'
            : tone === 'danger'
            ? isDark ? 'linear-gradient(135deg, rgba(185, 28, 28, 0.24), rgba(15, 23, 42, 0.76))' : 'linear-gradient(135deg, rgba(239, 68, 68, 0.12), rgba(255, 255, 255, 0.18))'
            : isDark
            ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.72), rgba(30, 41, 59, 0.56))'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.82), rgba(248, 250, 252, 0.72))',
        cursor: onClick ? 'pointer' : 'default',
        backdropFilter: 'blur(18px) saturate(160%)',
        WebkitBackdropFilter: 'blur(18px) saturate(160%)',
        boxShadow: onClick
          ? '0 14px 30px rgba(15, 23, 42, 0.08)'
          : '0 10px 24px rgba(15, 23, 42, 0.06)',
        transform: onClick ? 'translateY(0)' : 'none',
        animation: animateDelay > 0 ? `statusCardRise .45s ease-out ${animateDelay}ms both` : 'statusCardRise .45s ease-out both',
      }}
    >
      {compact ? (
        <div className="flex h-full flex-col">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.05em] text-gray-500 dark:text-gray-400">
                {title}
              </div>
            </div>
            <div className="shrink-0 text-right text-[10px] font-medium text-gray-500 dark:text-gray-400">
              <div>Updated</div>
              <div className="text-[11px] text-gray-600 dark:text-gray-300">{timestamp || 'No timestamp yet'}</div>
            </div>
          </div>
          <div className="mt-2">
            <StatusChip tone={tone} compact>{status}</StatusChip>
          </div>
          {children ? (
            <div className="mt-2 text-[12px] leading-5 text-gray-600 dark:text-gray-300">
              {children}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
              {title}
            </div>
            {statusAsChip ? (
              <div className="mt-2">
                <StatusChip tone={tone}>{status}</StatusChip>
              </div>
            ) : (
              <div className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                {status}
              </div>
            )}
            {children ? (
              <div className="mt-1 text-xs leading-5 text-gray-600 dark:text-gray-300">
                {children}
              </div>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1 text-right text-[11px] font-medium text-gray-500 dark:text-gray-400">
            <span>Updated</span>
            <span className="text-xs text-gray-600 dark:text-gray-300">
              {timestamp || 'No timestamp yet'}
            </span>
            {ctaLabel ? (
              <span className="mt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-blue-600 dark:text-blue-400">
                {ctaLabel}
              </span>
            ) : null}
          </div>
        </div>
      )}
    </CardElement>
  );
};

const JobStatusPanel = ({
  candidateCount = 0,
  selectedJobCandidateCount = 0,
  candidates = [],
  pipelineSnapshot = null,
  jobPostingPhase = 'idle',
  jobPostingProcessing = false,
  jobPostingComplete = false,
  jobPostingFailed = false,
  postedChannels = [],
  selectedChannels = [],
  availablePostingChannels = [],
  syncingPostedApplicants = false,
  postedApplicantsLastSyncTime = null,
  sourcingComplete = false,
  sourcingStats = null,
  talentScoutingStarted = false,
  outreachPhase = 'idle',
  outreachCompleted = false,
  agentStageLabel = '',
  jdText = '',
  selectedJobHasPostedJob = false,
  jobPostingLogs = [],
  sourcingLogs = [],
  outreachLogs = [],
  emailSyncState = null,
  selectedJobLastUpdated = null,
  onRefreshStatus = null,
  onNavigateTab = null,
  onOpenPipelineFilter = null,
  sessionId = '',
  searchId = '',
  interviewRefreshKey = 0,
  featureAccess = null,
}) => {
  const { isDark } = useTheme();
  const { settings } = useSettings();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [statusRefreshError, setStatusRefreshError] = useState('');
  const [syncDetailsOpen, setSyncDetailsOpen] = useState(false);
  const syncDetailsRef = useRef(null);
  const [databaseWorkflow, setDatabaseWorkflow] = useState({
    process: null,
    posting: null,
    candidateSummary: null,
    candidates: [],
  });
  const [mailSyncSettings, setMailSyncSettings] = useState({
    autoSyncEnabled: false,
    autoSyncFrequencySeconds: 3600,
  });
  const canUseFeature = (feature) => canUseCompanyFeature(featureAccess, feature);
  const showJobPosting = canUseFeature('job_posting');
  const showSourcing = canUseFeature('sourcing');
  const showApplicantSync = showJobPosting;
  const showEmailSync = canUseFeature('outreach') || canUseFeature('scheduling') || canUseFeature('email_sync');
  const showPipeline = canUseFeature('filter');

  useEffect(() => {
    if (!syncDetailsOpen) return undefined;
    const closeOnOutsideInteraction = (event) => {
      if (!syncDetailsRef.current?.contains(event.target)) {
        setSyncDetailsOpen(false);
      }
    };
    document.addEventListener('pointerdown', closeOnOutsideInteraction);
    return () => document.removeEventListener('pointerdown', closeOnOutsideInteraction);
  }, [syncDetailsOpen]);

  const loadDatabaseWorkflow = async () => {
    if (!sessionId) {
      setDatabaseWorkflow({ process: null, posting: null, candidateSummary: null, candidates: [] });
      return;
    }

    const [processResult, postingResult, candidatesResult] = await Promise.allSettled([
      aiAutomationApi.get(`/process-status/${encodeURIComponent(sessionId)}`),
      aiAutomationApi.get(`/job-postings/by-session/${encodeURIComponent(sessionId)}`),
      aiAutomationApi.get(`/automation_candidates/all?session_id=${encodeURIComponent(sessionId)}&limit=200`),
    ]);
    const candidatePayload = candidatesResult.status === 'fulfilled' ? candidatesResult.value : null;
    const candidateRows = candidatePayload
      ? (candidatePayload.data || candidatePayload.candidates || (Array.isArray(candidatePayload) ? candidatePayload : []))
      : [];
    const candidateSummary = candidatePayload
      ? {
        total: Number(candidatePayload.total_records ?? candidateRows.length ?? 0) || 0,
        emailReady: Number(candidatePayload.email_count ?? candidateRows.filter((candidate) => String(candidate?.email || '').trim()).length ?? 0) || 0,
        statusCounts: candidatePayload.status_counts || {},
      }
      : null;
    setDatabaseWorkflow({
      process: processResult.status === 'fulfilled' ? processResult.value : null,
      posting: postingResult.status === 'fulfilled' ? postingResult.value?.job_posting || null : null,
      candidateSummary,
      candidates: candidateRows,
    });
  };

  useEffect(() => {
    loadDatabaseWorkflow().catch(() => {
      setDatabaseWorkflow({ process: null, posting: null, candidateSummary: null, candidates: [] });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;
    const cid = getCid();
    if (!showEmailSync || !cid) {
      setMailSyncSettings({ autoSyncEnabled: false, autoSyncFrequencySeconds: 3600 });
      return undefined;
    }

    aiAutomationApi.get(`/email/settings/${cid}`)
      .then((data) => {
        if (cancelled) return;
        const settingsData = data?.settings || {};
        setMailSyncSettings({
          autoSyncEnabled: Boolean(settingsData.auto_sync_enabled),
          autoSyncFrequencySeconds: Number(settingsData.auto_sync_frequency_seconds || 3600) || 3600,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setMailSyncSettings({ autoSyncEnabled: false, autoSyncFrequencySeconds: 3600 });
        }
      });

    return () => { cancelled = true; };
  }, [showEmailSync]);

  const refreshStatus = async () => {
    if (refreshingStatus || typeof onRefreshStatus !== 'function') return;
    setRefreshingStatus(true);
    setStatusRefreshError('');
    try {
      await Promise.all([onRefreshStatus(), loadDatabaseWorkflow()]);
    } catch (error) {
      setStatusRefreshError(
        error?.message || 'Could not refresh status'
      );
    } finally {
      setRefreshingStatus(false);
    }
  };

  // ── Interview updates (job-scoped) — total / today / ongoing / completed ──────────────────
  const [interviewStats, setInterviewStats] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!searchId) { setInterviewStats(null); return undefined; }
    (async () => {
      try {
        const res = await aiAutomationApi.get(`/interviews/search/${encodeURIComponent(searchId)}/candidates-status`);
        const rows = Object.values(res?.already_scheduled_candidates || {});
        const todayIso = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
        let total = 0; let completed = 0; let today = 0; let ongoing = 0;
        rows.forEach((s) => {
          total += 1;
          const isCompleted = Boolean(s?.completed)
            || String(s?.interview_status || s?.status || '').toLowerCase() === 'interviewed'
            || String(s?.ai_status || '').toLowerCase() === 'completed';
          if (isCompleted) completed += 1; else ongoing += 1;   // ongoing = scheduled/booked, not yet done
          if (String(s?.scheduled_date || '').slice(0, 10) === todayIso) today += 1;
        });
        if (alive) setInterviewStats({ total, completed, today, ongoing });
      } catch {
        if (alive) setInterviewStats(null);
      }
    })();
    return () => { alive = false; };
  }, [searchId, interviewRefreshKey]);

  const effectiveCandidateCount = Math.max(Number(candidateCount) || 0, Number(selectedJobCandidateCount) || 0);
  const activePostedChannels = useMemo(
    () => (Array.isArray(postedChannels) ? postedChannels.filter(Boolean) : []),
    [postedChannels],
  );
  const activeSelectedChannels = useMemo(
    () => (Array.isArray(selectedChannels) ? selectedChannels.filter(Boolean) : []),
    [selectedChannels],
  );
  const pipelineCandidates = useMemo(
    () => {
      const databaseCandidates = Array.isArray(databaseWorkflow.candidates)
        ? databaseWorkflow.candidates.filter(Boolean)
        : [];
      if (databaseCandidates.length > 0) return databaseCandidates;
      return Array.isArray(candidates) ? candidates.filter(Boolean) : [];
    },
    [candidates, databaseWorkflow.candidates],
  );
  const emailSyncLogs = useMemo(
    () => (Array.isArray(emailSyncState?.logs) ? emailSyncState.logs : []),
    [emailSyncState?.logs],
  );
  const emailSyncPhase = String(emailSyncState?.phase || '').trim().toLowerCase();
  const pipelineSnapshotCounts = pipelineSnapshot && typeof pipelineSnapshot === 'object'
    ? (pipelineSnapshot.counts || pipelineSnapshot.pipeline_counts || pipelineSnapshot.status_counts || pipelineSnapshot.stage_counts || null)
    : null;
  const sourcingStatsSafe = sourcingStats && typeof sourcingStats === 'object' ? sourcingStats : {};
  const sourcingStatsTotal = Number(
    sourcingStatsSafe.total_profiles ||
    sourcingStatsSafe.totalProfiles ||
    sourcingStatsSafe.total_candidates ||
    sourcingStatsSafe.totalCandidates ||
    0
  ) || 0;
  const shouldUseSourcingStats = sourcingStatsTotal > 0 && (
    effectiveCandidateCount <= 0 || sourcingStatsTotal === effectiveCandidateCount
  );
  const sourcingEmailReadyCount = Number(
    shouldUseSourcingStats
      ? (
        sourcingStatsSafe.email_count ||
        sourcingStatsSafe.emailCount ||
        sourcingStatsSafe.contactable_count ||
        sourcingStatsSafe.contactableCount ||
        0
      )
      : 0
  ) || 0;
  const jobPostingTimestamp = getLatestTimestamp(jobPostingLogs) || (selectedJobLastUpdated ? new Date(selectedJobLastUpdated) : null);
  const sourcingTimestamp = getLatestTimestamp(sourcingLogs);
  const emailTimestamp = emailSyncState?.lastSync
    || emailSyncState?.completedAt
    || emailSyncState?.updatedAt
    || emailSyncState?.startedAt
    || getLatestTimestamp(emailSyncLogs);
  const applicantTimestamp = postedApplicantsLastSyncTime
    ? (postedApplicantsLastSyncTime instanceof Date ? postedApplicantsLastSyncTime : new Date(postedApplicantsLastSyncTime))
    : null;
  const hasJd = Boolean(String(jdText || '').trim());
  const postingPhase = String(jobPostingPhase || 'idle').toLowerCase();
  const persistedPosting = databaseWorkflow.posting;
  const trackedProcess = databaseWorkflow.process;
  const verifiedPostedChannels = Array.isArray(persistedPosting?.verified_posted_channels)
    ? persistedPosting.verified_posted_channels
    : [];
  const trackerValueIsProcessing = (value) => value === true || String(value || '').toLowerCase() === 'processing';
  const postIsProcessing = Boolean(
    trackerValueIsProcessing(trackedProcess?.job_post_linkedin)
    || trackerValueIsProcessing(trackedProcess?.job_post_naukri)
  );
  const sourceIsProcessing = Boolean(
    trackerValueIsProcessing(trackedProcess?.sourcing_google)
    || trackerValueIsProcessing(trackedProcess?.sourcing_linkedin)
    || trackerValueIsProcessing(trackedProcess?.sourcing_resumebox)
  );
  const sourceIsComplete = Boolean(
    trackedProcess?.sourcing_completed
    || trackedProcess?.sourcing_google_complete
    || trackedProcess?.sourcing_linkedin_complete
    || trackedProcess?.sourcing_resumebox_complete
  );
  const applicantSyncIsProcessing = Boolean(persistedPosting?.sync_process);
  const applicantSyncIsComplete = Boolean(persistedPosting?.last_sync);
  const dbCandidateSummary = databaseWorkflow.candidateSummary || null;
  const dbEmailReadyCount = Number(dbCandidateSummary?.emailReady || 0) || 0;

  const postingStatus = (() => {
    if (postIsProcessing) return { label: 'Posting in progress', tone: 'active' };
    if (postingPhase === 'collecting_details') return { label: 'Collecting details', tone: 'active' };
    if (postingPhase === 'channel_selection') return { label: 'Choosing channels', tone: 'warning' };
    if (verifiedPostedChannels.length > 0) {
      return {
        label: `Posted on ${verifiedPostedChannels.join(', ')}`,
        tone: 'success',
      };
    }
    if (jobPostingFailed) return { label: 'Needs attention', tone: 'danger' };
    return { label: hasJd ? 'Ready to post' : 'Waiting for JD', tone: 'neutral' };
  })();

  const navigateTab = (tab) => {
    if (typeof onNavigateTab === 'function') {
      onNavigateTab(tab);
    }
  };

  const openPipelineFilter = (statusFilter) => {
    if (!showPipeline) return;
    if (typeof onOpenPipelineFilter === 'function') {
      onOpenPipelineFilter(statusFilter);
      return;
    }
    navigateTab('sourcing');
  };

  const sourcingStatus = (() => {
    if (sourceIsProcessing) return { label: 'Sourcing in progress', tone: 'active' };
    if (sourceIsComplete) return { label: 'Sourcing complete', tone: 'success' };
    if (effectiveCandidateCount > 0) return { label: 'Candidates available', tone: 'warning' };
    return { label: 'Waiting for sourcing', tone: 'neutral' };
  })();

  const emailStatus = (() => {
    if (!emailSyncState) return { label: 'Ready', tone: 'neutral', detail: 'No sync state yet' };
    if (emailSyncState.syncing) return { label: 'Running', tone: 'active', detail: emailSyncState.progress || 'Sync in progress' };
    if (emailSyncPhase === 'error') {
      return {
        label: 'Failed',
        tone: 'danger',
        detail: emailSyncLogs[emailSyncLogs.length - 1]?.message || emailSyncState.progress || 'Sync failed',
      };
    }
    if (emailSyncPhase === 'complete' || emailSyncState.completedAt) {
      const resumeCount = Number(emailSyncState?.resumeParsed || 0);
      const total = Number(emailSyncState?.resumeTotal || 0);
      const resultCount = Number(emailSyncState?.syncResult?.new_count || 0);
      const parts = [];
      if (resultCount > 0) parts.push(`${resultCount} mail${resultCount === 1 ? '' : 's'}`);
      if (total > 0) parts.push(`${resumeCount}/${total} resumes`);
      else if (resumeCount > 0) parts.push(`${resumeCount} resume${resumeCount === 1 ? '' : 's'}`);
      return {
        label: 'Complete',
        tone: 'success',
        detail: parts.length > 0 ? parts.join(' · ') : 'Replies synced',
      };
    }
    return { label: 'Ready', tone: 'neutral', detail: emailSyncState.progress || 'Ready for subject-based mail sync' };
  })();

  const applicantStatus = (() => {
    if (applicantSyncIsProcessing) return { label: 'Running', tone: 'active', detail: 'Collecting applicants' };
    if (applicantSyncIsComplete) return { label: 'Complete', tone: 'success', detail: 'Applicants available' };
    if (verifiedPostedChannels.length > 0) return { label: 'Ready', tone: 'warning', detail: 'Starts after posting' };
    return { label: 'Waiting', tone: 'neutral', detail: 'Starts after posting' };
  })();

  const pipelineStatus = useMemo(() => {
    const counts = {
      imported: 0,
      notContacted: 0,
      contacted: 0,
      interested: 0,
      shortlisted: 0,
      interviewed: 0,
    };

    pipelineCandidates.forEach((candidate) => {
      const status = normalizeCandidateStatus(candidate);
      // Imported candidates are added directly for this job (no sourcing/outreach) — count them as
      // "Imported" while at their initial stage; once they progress (e.g. booked → Shortlisted) they
      // fall into that later stage below instead.
      const isImported = String(candidate?.source_type || candidate?.channel || '').toLowerCase() === 'imported';
      if (status.includes('interview') || status.includes('hired') || status.includes('final')) counts.interviewed += 1;
      else if (status.includes('shortlist')) counts.shortlisted += 1;
      else if (status.includes('interest')) counts.interested += 1;
      else if (status.includes('contact') || status.includes('respond') || status.includes('follow')) counts.contacted += 1;
      else if (isImported) counts.imported += 1;
      else counts.notContacted += 1;
    });

    const snapshotCountsSafe = dbCandidateSummary?.statusCounts || pipelineSnapshotCounts || {};
    const hasPersistedStageCounts = Object.keys(snapshotCountsSafe).length > 0;
    const snapshotNotContacted = Number(
      snapshotCountsSafe.not_contacted ||
      snapshotCountsSafe.sourced ||
      snapshotCountsSafe.identified ||
      snapshotCountsSafe.pending ||
      0
    ) || 0;
    const timestamp = getLatestTimestamp([
      ...pipelineCandidates
        .map((candidate) => ({ timestamp: getCandidateTimestamp(candidate) }))
        .filter((item) => item.timestamp),
      ...(pipelineSnapshot ? [{
        timestamp: pipelineSnapshot.updatedAt || pipelineSnapshot.updated_at || pipelineSnapshot.timestamp || pipelineSnapshot.lastUpdated || null,
      }] : []),
    ]);

    return {
      imported: pipelineCandidates.length > 0
        ? counts.imported
        : (Number(snapshotCountsSafe.imported) || 0),
      notContacted: pipelineCandidates.length > 0
        ? counts.notContacted
        : (hasPersistedStageCounts ? snapshotNotContacted : Math.max(dbEmailReadyCount, sourcingEmailReadyCount)),
      contacted: pipelineCandidates.length > 0
        ? counts.contacted
        : (Number(snapshotCountsSafe.contacted || snapshotCountsSafe.follow_up) || 0),
      interested: pipelineCandidates.length > 0
        ? counts.interested
        : (Number(snapshotCountsSafe.interested) || 0),
      shortlisted: pipelineCandidates.length > 0
        ? counts.shortlisted
        : (Number(snapshotCountsSafe.shortlisted || snapshotCountsSafe.shortlist) || 0),
      interviewed: pipelineCandidates.length > 0
        ? counts.interviewed
        : (Number(snapshotCountsSafe.interviewed || snapshotCountsSafe.interview || snapshotCountsSafe.hired) || 0),
      timestamp,
    };
  }, [pipelineCandidates, pipelineSnapshot, pipelineSnapshotCounts, sourcingEmailReadyCount, dbCandidateSummary, dbEmailReadyCount]);

  const notContactedEmailCounts = useMemo(() => {
    if (pipelineCandidates.length > 0) {
      return pipelineCandidates.reduce((summary, candidate) => {
        const status = normalizeCandidateStatus(candidate);
        const isNotContacted = !(
          status.includes('contact') || status.includes('respond') || status.includes('follow')
          || status.includes('interest') || status.includes('shortlist') || status.includes('interview')
          || status.includes('hired') || status.includes('final')
        );
        if (!isNotContacted) return summary;
        if (String(candidate?.email || '').trim()) summary.withEmail += 1;
        else summary.withoutEmail += 1;
        return summary;
      }, { withEmail: 0, withoutEmail: 0 });
    }
    const withEmail = Math.min(pipelineStatus.notContacted, dbEmailReadyCount || sourcingEmailReadyCount);
    return { withEmail, withoutEmail: Math.max(pipelineStatus.notContacted - withEmail, 0) };
  }, [pipelineCandidates, pipelineStatus.notContacted, dbEmailReadyCount, sourcingEmailReadyCount]);

  const syncedCandidateEmails = useMemo(() => {
    const summaries = emailSyncState?.syncResult?.reply_summaries || [];
    const replies = emailSyncState?.syncResult?.new_replies || [];
    const fromLogs = emailSyncLogs.map((entry) => entry?.candidate_email).filter(Boolean);
    return [...new Set([
      ...summaries.map((item) => item?.candidate_email),
      ...replies.map((item) => item?.candidate_email || item?.sender),
      ...fromLogs,
    ].map((value) => String(value || '').trim()).filter(Boolean))];
  }, [emailSyncState?.syncResult, emailSyncLogs]);

  const pipelineNextAction = useMemo(() => {
    if (!showPipeline) return '';
    if (pipelineStatus.interviewed > 0) {
      return 'Review completed interviews and decide the final hiring outcome.';
    }
    if (pipelineStatus.shortlisted > 0) {
      return 'Review shortlisted candidates and continue the hiring process.';
    }
    if (pipelineStatus.interested > 0) {
      return 'Review interested candidates and shortlist the best matches.';
    }
    if (pipelineStatus.contacted > 0) {
      return 'Review contacted candidates and follow up where needed.';
    }
    if (pipelineStatus.notContacted > 0) {
      return 'Start outreach for candidates who have not been contacted.';
    }
    return 'Pipeline is quiet. Move a candidate into the next stage.';
  }, [pipelineStatus, showPipeline]);
  const pipelineActionCount = pipelineStatus.imported + pipelineStatus.notContacted + pipelineStatus.contacted + pipelineStatus.interested;
  const pipelineTotalCount = pipelineActionCount + pipelineStatus.shortlisted + pipelineStatus.interviewed;
  const hasPipelineActivity = pipelineTotalCount > 0;
  const inactivePipelineCount = Math.max(
    effectiveCandidateCount - pipelineTotalCount,
    0
  );

  const derivedState = useMemo(() => {
    const currentProcess = (() => {
      if (syncingPostedApplicants) return 'Synchronizing posted-job applicants into the candidate pool.';
      if (emailSyncState?.syncing) return 'Synchronizing email replies into the candidate workflow.';
      if (jobPostingProcessing || postingPhase === 'posting') return 'Publishing the job to the selected channels.';
      if (postingPhase === 'collecting_details') return 'Collecting the final job details before posting.';
      if (postingPhase === 'channel_selection') return 'Confirming which channels to use for the post.';
      if (talentScoutingStarted && !sourcingComplete) return 'Searching and ranking candidates across sourcing channels.';
      if (outreachPhase && outreachPhase !== 'idle') return outreachCompleted ? 'Outreach is complete and follow-up actions are ready.' : 'Running outreach to shortlisted candidates.';
      if (sourcingComplete && effectiveCandidateCount > 0) return 'Candidate sourcing is complete and the pipeline is ready for review.';
      if (activePostedChannels.length > 0) return 'The job is live and awaiting the next pipeline step.';
      if (hasJd) return 'The job is prepared and ready for the next action.';
      return 'Waiting for job details to be completed.';
    })();

    const nextStep = (() => {
      if (!hasJd) return 'Finish the job details, then post the role.';
      if (postingPhase === 'collecting_details') return 'Complete the remaining details, then publish the job.';
      if (jobPostingProcessing || postingPhase === 'posting') return 'Wait for posting to finish, then sync applicants.';
      if (activePostedChannels.length > 0 && !syncingPostedApplicants && !postedApplicantsLastSyncTime) return 'Sync applicants so candidates appear in the sourcing panel.';
      if (talentScoutingStarted && !sourcingComplete) return 'Let sourcing finish, then review matching candidates.';
      if (outreachPhase && outreachPhase !== 'idle' && !outreachCompleted) return 'Let outreach complete, then move to interview scheduling.';
      if (sourcingComplete && effectiveCandidateCount > 0 && (outreachPhase === 'idle' || !outreachPhase)) return 'Open candidates and start outreach.';
      if (effectiveCandidateCount === 0 && activePostedChannels.length === 0) return 'Post the job to begin collecting candidates.';
      return 'Continue the current workflow or switch to the relevant tab.';
    })();

    return { currentProcess, nextStep };
  }, [
    activePostedChannels,
    effectiveCandidateCount,
    hasJd,
    jobPostingProcessing,
    emailSyncState?.syncing,
    postingPhase,
    sourcingComplete,
    talentScoutingStarted,
    syncingPostedApplicants,
    outreachPhase,
    outreachCompleted,
    postedApplicantsLastSyncTime,
  ]);

  const prepareWorkflowComplete = Boolean(hasJd);
  const postWorkflowComplete = verifiedPostedChannels.length > 0;
  const syncWorkflowComplete = applicantSyncIsComplete;
  const sourceWorkflowComplete = sourceIsComplete;
  const reviewWorkflowReady = prepareWorkflowComplete
    && (!showJobPosting || postWorkflowComplete)
    && (!showApplicantSync || syncWorkflowComplete)
    && (!showSourcing || sourceWorkflowComplete);

  const workflowStages = [
    { label: 'Prepare', state: prepareWorkflowComplete ? 'complete' : 'active' },
    ...(showJobPosting ? [{
      label: 'Post',
      state: postIsProcessing
        ? 'active'
        : postWorkflowComplete
        ? 'complete'
        : 'upcoming',
    }] : []),
    ...(showApplicantSync ? [{
      label: 'Sync',
      state: applicantSyncIsProcessing
        ? 'active'
        : syncWorkflowComplete
        ? 'complete'
        : 'upcoming',
    }] : []),
    ...(showSourcing ? [{
      label: 'Source',
      state: sourceIsProcessing
        ? 'active'
        : sourceWorkflowComplete
        ? 'complete'
        : 'upcoming',
    }] : []),
    {
      label: 'Review',
      state: reviewWorkflowReady ? 'active' : 'upcoming',
    },
  ];

  return (
    <div className="mt-0">
      <ChatCard
        variant="primary"
        size="lg"
        rounded="xl"
        className="chat_no_shadow"
        style={{
          borderColor: isDark ? 'rgba(148, 163, 184, 0.22)' : 'rgba(148, 163, 184, 0.24)',
          background: isDark
            ? `linear-gradient(180deg, rgba(15, 23, 42, 0.82) 0%, rgba(15, 23, 42, 0.70) 100%), radial-gradient(circle at top left, ${primaryColor}22 0%, transparent 38%), radial-gradient(circle at top right, ${secondaryColor}18 0%, transparent 32%)`
            : `linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(250, 251, 253, 0.90) 100%), radial-gradient(circle at top left, ${primaryColor}12 0%, transparent 28%), radial-gradient(circle at top right, ${secondaryColor}10 0%, transparent 24%)`,
          backdropFilter: 'blur(18px) saturate(160%)',
          WebkitBackdropFilter: 'blur(18px) saturate(160%)',
          boxShadow: isDark
            ? '0 24px 60px rgba(15, 23, 42, 0.28)'
            : '0 24px 60px rgba(59, 91, 219, 0.10)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <style>{`
          @keyframes statusPanelFadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes statusCardRise {
            from { opacity: 0; transform: translateY(8px) scale(0.99); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes statusCountPop {
            0% { transform: scale(0.92); opacity: 0; }
            60% { transform: scale(1.06); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes workflowStepGlow {
            0%, 100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
            50% { box-shadow: 0 8px 24px rgba(59, 130, 246, 0.08); }
          }
          @media (prefers-reduced-motion: reduce) {
            .status-panel-motion,
            .status-card-motion,
            .status-count-motion,
            .workflow-step-motion {
              animation: none !important;
              transition: none !important;
            }
          }
        `}</style>
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${primaryColor}12 0%, transparent 22%, ${secondaryColor}08 100%)`,
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', zIndex: 1 }} className="status-panel-motion">
          <div
            className="rounded-2xl border px-2.5 pt-2 pb-2 shadow-sm backdrop-blur-sm"
            style={{
              borderColor: isDark ? 'rgba(99, 102, 241, 0.38)' : 'rgba(226, 232, 240, 0.82)',
              background: isDark
                ? 'linear-gradient(180deg, rgba(17, 24, 39, 0.96), rgba(15, 23, 42, 0.94))'
                : 'rgba(255, 255, 255, 0.80)',
            }}
          >
            <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5">
              {workflowStages.map(({ label, state }, index) => {
                const isActive = state === 'active';
                const isComplete = state === 'complete';
                return (
                  <div
                    key={label}
                    className="workflow-step-motion flex min-w-[62px] flex-1 flex-col items-center rounded-xl border px-2 py-1.5 transition-all duration-200 hover:-translate-y-1 hover:shadow-md hover:border-blue-300"
                    style={{
                      borderColor: isActive
                        ? isDark ? `${primaryColor}B3` : 'rgba(37, 99, 235, 0.22)'
                        : isComplete
                        ? isDark ? 'rgba(74, 222, 128, 0.42)' : 'rgba(34, 197, 94, 0.16)'
                        : isDark ? 'rgba(100, 116, 139, 0.38)' : 'rgba(148, 163, 184, 0.12)',
                      background: isActive
                        ? isDark ? `${primaryColor}38` : 'rgba(37, 99, 235, 0.08)'
                        : isComplete
                        ? isDark ? 'rgba(22, 101, 52, 0.28)' : 'rgba(34, 197, 94, 0.08)'
                        : isDark ? 'rgba(30, 41, 59, 0.88)' : 'rgba(255, 255, 255, 0.58)',
                      backdropFilter: 'blur(10px) saturate(130%)',
                      WebkitBackdropFilter: 'blur(10px) saturate(130%)',
                      animation: `statusCardRise .45s ease-out ${index * 70}ms both`,
                    }}
                  >
                    <div
                      className="status-count-motion flex h-5.5 w-5.5 items-center justify-center rounded-full text-[11px] font-bold"
                      style={{
                        backgroundColor: isActive
                          ? '#2563EB'
                          : isComplete
                          ? '#16A34A'
                          : isDark ? '#334155' : 'rgba(148, 163, 184, 0.16)',
                        color: isActive || isComplete ? '#fff' : isDark ? '#CBD5E1' : '#64748B',
                      }}
                    >
                      <WorkflowStepIcon state={state} />
                    </div>
                    <div className={`mt-1 text-center text-[9px] font-semibold uppercase tracking-[0.08em] ${isActive ? 'text-blue-700 dark:text-blue-200' : isComplete ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-600 dark:text-slate-300'}`}>
                      {label}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-1 hidden text-xs text-gray-600 dark:text-gray-300">
              {derivedState.currentProcess}
            </div>
            <div className="mt-0.5 hidden text-xs text-gray-500 dark:text-gray-400">
              Next: {derivedState.nextStep}
              {agentStageLabel ? ` · ${agentStageLabel}` : ''}
            </div>
          </div>

        <div className="mt-4 grid gap-2">
          {showJobPosting && (
            <SectionCard
              title="Job Posting Status"
              status={postingStatus.label}
              timestamp={formatTimestamp(jobPostingTimestamp)}
              tone={postingStatus.tone}
              ctaLabel="Open Job Posting"
              onClick={() => navigateTab('job_posting')}
              statusAsChip
              className="status-card-motion"
              animateDelay={40}
            >
              {postingStatus.label.startsWith('Posted on') && jobPostingFailed
                ? 'Posted, but one or more channels still need review'
                : selectedChannels.length > 0
                ? `${selectedChannels.length} channel${selectedChannels.length === 1 ? '' : 's'}`
                : `${activeSelectedChannels.length || availablePostingChannels.length || 0} channel${(activeSelectedChannels.length || availablePostingChannels.length || 0) === 1 ? '' : 's'}`
              }
            </SectionCard>
          )}

          {showPipeline && (
          <div
            className="status-card-motion rounded-2xl border px-4 py-3"
            style={{
              borderColor: isDark ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.18)',
              background: isDark
                ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.68), rgba(30, 41, 59, 0.48))'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.82), rgba(248, 250, 252, 0.72))',
              backdropFilter: 'blur(18px) saturate(160%)',
              WebkitBackdropFilter: 'blur(18px) saturate(160%)',
              boxShadow: '0 14px 30px rgba(15, 23, 42, 0.06)',
              animation: 'statusCardRise .45s ease-out 90ms both',
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                  Pipeline Status
                </div>
                <div className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                  Candidate flow at a glance
                </div>
                {effectiveCandidateCount > 0 ? (
                  <div className="mt-1 hidden text-xs text-gray-500 dark:text-gray-400">
                    {effectiveCandidateCount} candidate{effectiveCandidateCount === 1 ? '' : 's'} total
                  </div>
                ) : null}
                {inactivePipelineCount > 0 ? (
                  <div className="mt-0.5 hidden text-[11px] text-gray-500 dark:text-gray-400">
                    {inactivePipelineCount} candidate{inactivePipelineCount === 1 ? '' : 's'} not in active pipeline
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={refreshStatus}
                disabled={refreshingStatus || typeof onRefreshStatus !== 'function'}
                title={statusRefreshError || 'Refresh pipeline status'}
                aria-label="Refresh pipeline status"
                className="flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 transition-colors hover:border-blue-300 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
              >
                <span aria-hidden="true" className={refreshingStatus ? 'inline-block animate-spin' : 'inline-block'}>↻</span>
                <span>{refreshingStatus ? 'Refreshing' : 'Refresh'}</span>
              </button>
            </div>

            <div
              className="mt-4 grid gap-3"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))' }}
            >
              {[
                ...(pipelineStatus.imported > 0 ? [{
                  label: 'Imported',
                  value: pipelineStatus.imported,
                  tone: 'active',
                  hint: 'Added directly — ready to schedule',
                  statusFilter: ['sourced'],
                  info: 'Candidates imported for this job (no sourcing or outreach). They move straight to Shortlisted when scheduled.',
                }] : []),
                {
                  label: 'Not contacted',
                  value: pipelineStatus.notContacted,
                  tone: 'active',
                  hint: 'Ready for outreach',
                  statusFilter: ['sourced'],
                  info: 'Candidates in the Sourced stage who have not received outreach.',
                },
                {
                  label: 'Contacted',
                  value: pipelineStatus.contacted,
                  tone: 'warning',
                  hint: 'Outreach sent',
                  statusFilter: ['contacted'],
                  info: 'Candidates whose current stage is Contacted after outreach was sent.',
                },
                {
                  label: 'Interested',
                  value: pipelineStatus.interested,
                  tone: 'success',
                  hint: 'Ready for scheduling',
                  statusFilter: ['interested'],
                  info: 'Candidates with a positive response whose current stage is Interested.',
                },
                {
                  label: 'Shortlisted',
                  value: pipelineStatus.shortlisted,
                  tone: 'active',
                  hint: 'Ready for interview',
                  statusFilter: ['shortlisted'],
                  info: 'Candidates reviewed and moved to Shortlisted, ready for an interview.',
                },
                {
                  label: 'Interviewed',
                  value: pipelineStatus.interviewed,
                  tone: 'success',
                  hint: 'Interview completed',
                  statusFilter: ['interviewed'],
                  info: 'Candidates whose current stage is Interviewed, Hired, or Finalised.',
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="status-card-motion rounded-2xl border px-3 py-3 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                  style={{
                    borderColor:
                      item.tone === 'success'
                        ? 'rgba(34, 197, 94, 0.18)'
                        : item.tone === 'warning'
                        ? 'rgba(245, 158, 11, 0.18)'
                        : 'rgba(59, 130, 246, 0.16)',
                    background:
                      item.tone === 'success'
                        ? isDark ? 'linear-gradient(180deg, rgba(21, 128, 61, 0.24) 0%, rgba(15, 23, 42, 0.72) 100%)' : 'linear-gradient(180deg, rgba(34, 197, 94, 0.10) 0%, rgba(255, 255, 255, 0.06) 100%)'
                        : item.tone === 'warning'
                        ? isDark ? 'linear-gradient(180deg, rgba(180, 83, 9, 0.24) 0%, rgba(15, 23, 42, 0.72) 100%)' : 'linear-gradient(180deg, rgba(245, 158, 11, 0.10) 0%, rgba(255, 255, 255, 0.06) 100%)'
                        : isDark ? 'linear-gradient(180deg, rgba(30, 64, 175, 0.24) 0%, rgba(15, 23, 42, 0.72) 100%)' : 'linear-gradient(180deg, rgba(59, 130, 246, 0.10) 0%, rgba(255, 255, 255, 0.06) 100%)',
                    minHeight: '142px',
                    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.04)',
                    animation: `statusCardRise .45s ease-out ${120 + (item.label === 'Contacted' ? 40 : item.label === 'Interested' ? 80 : item.label === 'Shortlisted' ? 120 : 0)}ms both`,
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <div className="min-w-0 text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
                          {item.label}
                        </div>
                        <InfoIcon label={item.info} />
                      </div>
                      <div className="mt-2 text-sm font-semibold leading-5 text-gray-900 dark:text-white">
                        {item.hint}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="status-count-motion inline-flex h-8 min-w-8 shrink-0 items-center justify-center rounded-full px-2.5 text-sm font-bold leading-none transition-transform duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400"
                      onClick={(event) => {
                        event.stopPropagation();
                        openPipelineFilter(item.statusFilter);
                      }}
                      aria-label={`Open ${item.label.toLowerCase()} candidates in sourcing`}
                      style={{
                        color: item.tone === 'success' ? '#16A34A' : item.tone === 'warning' ? '#D97706' : '#2563EB',
                        background: item.tone === 'success' ? 'rgba(34, 197, 94, 0.12)' : item.tone === 'warning' ? 'rgba(245, 158, 11, 0.12)' : 'rgba(59, 130, 246, 0.12)',
                        cursor: 'pointer',
                        border: 'none',
                      }}
                    >
                      {item.value}
                    </button>
                  </div>
                  <div className="mt-2 text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                    {item.value === 1 ? '1 candidate' : `${item.value} candidates`}
                  </div>
                  {item.label === 'Not contacted' && (
                    <div className="mt-2 inline-flex max-w-full items-center overflow-hidden rounded-lg border border-slate-200 bg-white/70 text-[10px] font-semibold dark:border-slate-700 dark:bg-slate-900/45">
                      <span className="inline-flex items-center gap-1 border-r border-slate-200 px-2 py-1 text-emerald-700 dark:border-slate-700 dark:text-emerald-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
                        Email <strong>{notContactedEmailCounts.withEmail}</strong>
                      </span>
                      <span className="px-2 py-1 text-slate-500 dark:text-slate-400">
                        Missing <strong>{notContactedEmailCounts.withoutEmail}</strong>
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-3 rounded-xl border px-3 py-2" style={{
              borderColor: isDark ? 'rgba(71, 85, 105, 0.28)' : 'rgba(226, 232, 240, 0.95)',
              background: isDark ? 'rgba(15, 23, 42, 0.45)' : 'rgba(255, 255, 255, 0.65)',
            }}>
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                Next action
              </div>
              <div className="mt-1 text-sm text-gray-900 dark:text-white">
                {pipelineNextAction}
              </div>
            </div>
          </div>
          )}

          {showSourcing && !hasPipelineActivity && effectiveCandidateCount === 0 && (
            <SectionCard
              title="Sourcing Status"
              status={sourcingStatus.label}
              timestamp={formatTimestamp(sourcingTimestamp)}
              tone={sourcingStatus.tone}
              ctaLabel="Open Sourcing"
              onClick={() => navigateTab('sourcing')}
            >
              {sourcingStatus.label === 'Sourcing complete'
                ? `${effectiveCandidateCount} candidate${effectiveCandidateCount === 1 ? '' : 's'} available`
                : sourcingStatus.label === 'Sourcing in progress'
                ? 'Searching and ranking candidates across sourcing channels'
                : 'No sourcing activity yet'}
            </SectionCard>
          )}

          {(showApplicantSync || showEmailSync) && (
          <div className="mt-1 rounded-2xl border px-4 py-3"
            style={{
              borderColor: isDark ? 'rgba(148, 163, 184, 0.16)' : 'rgba(148, 163, 184, 0.16)',
              background: isDark
                ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.48) 0%, rgba(30, 41, 59, 0.34) 100%)'
                : 'linear-gradient(180deg, rgba(255, 255, 255, 0.78) 0%, rgba(248, 250, 252, 0.68) 100%)',
              backdropFilter: 'blur(16px) saturate(150%)',
              WebkitBackdropFilter: 'blur(16px) saturate(150%)',
              boxShadow: '0 12px 28px rgba(15, 23, 42, 0.04)',
            }}
          >
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
              Sync Activity
            </div>
            <div
              className="mt-3 grid gap-2"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 170px), 1fr))' }}
            >
              {showApplicantSync && (
                <SectionCard
                  title="Applicant Sync"
                  status={applicantStatus.label}
                  timestamp={formatTimestamp(applicantTimestamp)}
                  tone={applicantStatus.tone}
                  statusAsChip
                  compact
                  onClick={showSourcing ? () => navigateTab('sourcing') : undefined}
                >
                  {applicantStatus.detail}
                </SectionCard>
              )}

              {showEmailSync && (
              <SectionCard
                title="Mail Sync Based on Subject"
                status={emailStatus.label}
                timestamp={formatTimestamp(emailTimestamp)}
                tone={emailStatus.tone}
                statusAsChip
                compact
              >
                <div>{emailStatus.detail}</div>
                <div ref={syncDetailsRef} className="group relative mt-2 inline-flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-emerald-700 dark:text-emerald-300">
                    Synced candidates
                  </span>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setSyncDetailsOpen((value) => !value);
                    }}
                    aria-expanded={syncDetailsOpen}
                    aria-label={`${syncedCandidateEmails.length} synced candidates. Show details`}
                    className="inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-emerald-100 px-2 text-xs font-bold text-emerald-700 transition hover:bg-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-emerald-950/60 dark:text-emerald-300"
                  >
                    {syncedCandidateEmails.length}
                  </button>
                  <div
                    className={`${syncDetailsOpen ? 'block' : 'hidden group-hover:block'} absolute bottom-9 right-0 z-40 w-64 max-w-[min(16rem,calc(100vw-2rem))] rounded-xl border border-slate-200 bg-white p-3 text-left shadow-2xl dark:border-slate-700 dark:bg-slate-900`}
                  >
                    <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                      Synced candidate replies
                    </div>
                    <div className="mt-2 max-h-32 overflow-y-auto pr-1">
                      {syncedCandidateEmails.length > 0 ? syncedCandidateEmails.map((email) => (
                        <div key={email} className="border-b border-slate-100 py-1.5 text-[11px] font-medium text-slate-700 last:border-0 dark:border-slate-800 dark:text-slate-200">
                          {email} sent mail
                        </div>
                      )) : (
                        <div className="py-1 text-[11px] text-slate-500 dark:text-slate-400">No candidate replies synced yet</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-bold"
                    style={{
                      backgroundColor: mailSyncSettings.autoSyncEnabled
                        ? (isDark ? 'rgba(34,197,94,0.16)' : 'rgba(220,252,231,0.95)')
                        : (isDark ? 'rgba(148,163,184,0.14)' : 'rgba(241,245,249,0.95)'),
                      color: mailSyncSettings.autoSyncEnabled ? '#15803D' : (isDark ? '#CBD5E1' : '#64748B'),
                    }}
                  >
                    Auto sync {mailSyncSettings.autoSyncEnabled ? 'On' : 'Off'}
                  </span>
                  <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                    Every {formatInterval(mailSyncSettings.autoSyncFrequencySeconds)}
                  </span>
                  <InfoIcon label="Change auto sync from Account Settings > Mail Settings > Auto Sync. Enable it and set the sync frequency there." />
                </div>
              </SectionCard>
              )}
            </div>
            </div>
          )}

          {/* {interviewStats && (
            <div className="mt-1 rounded-2xl border px-4 py-3"
              style={{
                borderColor: isDark ? 'rgba(148, 163, 184, 0.16)' : 'rgba(148, 163, 184, 0.16)',
                background: isDark
                  ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.48) 0%, rgba(30, 41, 59, 0.34) 100%)'
                  : 'linear-gradient(180deg, rgba(255, 255, 255, 0.78) 0%, rgba(248, 250, 252, 0.68) 100%)',
                backdropFilter: 'blur(16px) saturate(150%)',
                WebkitBackdropFilter: 'blur(16px) saturate(150%)',
                boxShadow: '0 12px 28px rgba(15, 23, 42, 0.04)',
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                  Interview updates
                </div>
                {typeof onNavigateTab === 'function' && interviewStats.total > 0 && (
                  <button type="button" onClick={() => navigateTab('interview')}
                    className="text-[11px] font-semibold text-blue-600 transition hover:underline dark:text-blue-300">
                    Open pipeline →
                  </button>
                )}
              </div>
              {interviewStats.total === 0 ? (
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  No interviews scheduled for this job yet.
                </div>
              ) : (
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {[
                    { label: 'Total', value: interviewStats.total, color: '#2563EB', bg: 'rgba(59, 130, 246, 0.10)', border: 'rgba(59, 130, 246, 0.18)' },
                    { label: 'Today', value: interviewStats.today, color: '#D97706', bg: 'rgba(245, 158, 11, 0.10)', border: 'rgba(245, 158, 11, 0.18)' },
                    { label: 'Ongoing', value: interviewStats.ongoing, color: '#7C3AED', bg: 'rgba(124, 58, 237, 0.10)', border: 'rgba(124, 58, 237, 0.18)' },
                    { label: 'Completed', value: interviewStats.completed, color: '#16A34A', bg: 'rgba(34, 197, 94, 0.10)', border: 'rgba(34, 197, 94, 0.18)' },
                  ].map((m) => (
                    <div key={m.label} className="status-card-motion rounded-xl border px-3 py-2.5"
                      style={{ borderColor: m.border, background: `linear-gradient(180deg, ${m.bg} 0%, rgba(255,255,255,0.04) 100%)` }}>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
                        {m.label}
                      </div>
                      <div className="mt-1 text-2xl font-bold leading-none" style={{ color: m.color }}>
                        {m.value}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )} */}
          </div>

        </div>
      </ChatCard>
    </div>
  );
};

export default JobStatusPanel;
